import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import classNames from 'classnames';

import {
    LegacyCityPropType,
    HorizontalTabs,
    Form,
    HiddenField,
    getLanguageFromLocale,
} from '@ratehub/base-ui';
import {
    SCENARIO_TYPES,
    useApplyForRateCallback,
} from '@ratehub/mtg-common';
import { INTENDED_USE } from '@ratehub/documents/mtg-journey-document/stable';

import fetchProductTableBestMortgageRates from '../functions/fetchProductTableBestMortgageRates';
import PAGE_TEMPLATES from '../definitions/PageTemplates';
import BestMortgageRatesPropType from '../definitions/BestMortgageRatesPropType';
import ProductTableHeader, { PRODUCT_TABLE_TITLE_ELEMENT_TYPES } from './ProductTableHeader';
import ProductTableBestMortgageRatesTable from './ProductTableBestMortgageRatesTable';
import ProductTableMortgageFilteredAsyncContainer from './ProductTableMortgageFilteredAsyncContainer';


function ProductTableBestMortgageRates({
    title,
    titleAs = 'h2',
    isTitleHidden = false,
    lastUpdatedDate,

    initialFilterValues = {},
    initialRates,

    ...otherProps
}) {
    const intl = useIntl();
    const applyForRate = useApplyForRateCallback();

    const [ formState, setFormState ] = useState(null);
    const [ selectedTab, setSelectedTab ] = useState(TABLE_OPTIONS.MARKET_RATES);

    const filterValues = {
        ...DEFAULT_FILTER_VALUES,
        ...initialFilterValues,
        // we receive downPaymentPercent from props, but the filter component expects downPayment.percent
        downPayment: initialFilterValues?.downPaymentPercent
            ? { percent: initialFilterValues.downPaymentPercent }
            : DEFAULT_FILTER_VALUES.downPayment,
    };

    const rateFetchingOptions = {
        city: formState?.values?.scenario === SCENARIO_TYPES.HELOC
            ? formState?.values?.helocCity
            : formState?.values?.city,
        scenario: formState?.values?.scenario,
        amortization: formState?.values?.scenario === SCENARIO_TYPES.RENEWAL
            ? ~~(formState?.values?.renewAmortization ?? DEFAULT_FILTER_VALUES.renewAmortization)
            : ~~(formState?.values?.purchaseRefinanceAmortization ?? DEFAULT_FILTER_VALUES.purchaseRefinanceAmortization),
        isRental: formState?.values?.scenario !== SCENARIO_TYPES.HELOC
            && (formState?.values?.occupancy === INTENDED_USE.RENTAL
                || formState?.values?.occupancy === INTENDED_USE.SECOND_HOME),
    };

    function handleRateClick(rate) {
        applyForRate({
            rate,
            rateFetchingOptions,
        });
    }

    return (
        <Container {...otherProps}>
            <ProductTableHeader
                title={isTitleHidden ? undefined : title}
                titleAs={titleAs}
                lastUpdatedDate={lastUpdatedDate}
            />

            <HorizontalTabs
                tabs={TABS}
                onTabChange={setSelectedTab}
                tabPanelIdPrefix="BMRTable-tab-panel"
                tabIdPrefix="BMRTable-tab"
                isCondensed={true}
            />

            <Form
                onCreateFormState={setFormState}
                className="rh-mt-1"
            >
                <HiddenField
                    name="language"
                    value={getLanguageFromLocale(intl.locale)}
                />

                <ProductTableMortgageFilteredAsyncContainer
                    values={filterValues}
                    excludedFieldNames={[ 'type', 'term', 'paymentFrequency' ]}

                    fetchedValueName="rates"
                    initialFetchedValue={initialRates}

                    getFormValues={getFormValuesForFetch}
                    onFetchValue={fetchProductTableBestMortgageRates}
                    debounce={400}
                >
                    {(values) => (
                        <>
                            <ProductTableBestMortgageRatesTable
                                className={classNames({ 'hidden-table': selectedTab !== TABLE_OPTIONS.MARKET_RATES })}
                                aria-hidden={selectedTab !== TABLE_OPTIONS.MARKET_RATES}
                                rates={values?.rates?.bestMarketRates ?? initialRates?.bestMarketRates ?? []}
                                onRateClick={handleRateClick}
                            />

                            <ProductTableBestMortgageRatesTable
                                className={classNames({ 'hidden-table': selectedTab !== TABLE_OPTIONS.BANK_RATES })}
                                aria-hidden={selectedTab !== TABLE_OPTIONS.BANK_RATES}
                                rates={values?.rates?.bestBankRates ?? initialRates?.bestBankRates ?? []}
                                onRateClick={handleRateClick}
                            />
                        </>
                    )}
                </ProductTableMortgageFilteredAsyncContainer>
            </Form>
        </Container>
    );
}

ProductTableBestMortgageRates.propTypes = {
    title: PropTypes.string,
    titleAs: PropTypes.oneOf(PRODUCT_TABLE_TITLE_ELEMENT_TYPES),
    isTitleHidden: PropTypes.bool,
    lastUpdatedDate: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
    ]),

    initialFilterValues: PropTypes.shape({
        scenario: PropTypes.string,
        province: PropTypes.string,
        city: LegacyCityPropType,
        homePrice: PropTypes.number,
        downPaymentPercent: PropTypes.number,
        renewMortgageBalance: PropTypes.number,
        refinanceMortgageBalance: PropTypes.number,
        helocAmount: PropTypes.number,
        additionalFunds: PropTypes.number,
        occupancy: PropTypes.oneOf(Object.values(INTENDED_USE)),
        isCMHC: PropTypes.bool,
        renewAmortization: PropTypes.number,
        purchaseRefinanceAmortization: PropTypes.number,
    }),

    initialRates: PropTypes.shape({
        bestMarketRates: BestMortgageRatesPropType,
        bestBankRates: BestMortgageRatesPropType,
    }),
};

function getFormValuesForFetch(values) {
    return {
        language: values?.language,
        scenario: values?.scenario,
        city: values?.city,
        helocCity: values?.helocCity,
        homePrice: values?.homePrice,
        downPaymentPercent: values?.downPayment?.percent,
        renewMortgageBalance: values?.renewMortgageBalance,
        refinanceMortgageBalance: values?.refinanceMortgageBalance,
        helocAmount: values?.helocAmount,
        additionalFunds: values?.additionalFunds,
        occupancy: values?.occupancy,
        isCMHC: values?.isCMHC,
        renewAmortization: values?.renewAmortization,
        purchaseRefinanceAmortization: values?.purchaseRefinanceAmortization,
    };
}

const DEFAULT_FILTER_VALUES = {
    scenario: SCENARIO_TYPES.PURCHASE,
    province: undefined,
    city: undefined,
    homePrice: 400_000,
    downPayment: { percent: 5 },
    renewMortgageBalance: 350_000,
    refinanceMortgageBalance: 300_000,
    helocAmount: 200_000,
    additionalFunds: 0,
    occupancy: INTENDED_USE.OWNER_OCCUPIED,
    isCMHC: true,
    renewAmortization: 20,
    purchaseRefinanceAmortization: 25,
};

const TABLE_OPTIONS = {
    MARKET_RATES: 'best-market-rates',
    BANK_RATES: 'best-bank-rates',
};

const TABS = [
    {
        value: TABLE_OPTIONS.MARKET_RATES,
        label: (
            <FormattedMessage
                id="web-components.ProductTableBestMortgageRates.BestMarketRatesTab"
                defaultMessage="Best market rates"
            />
        ),
    },
    {
        value: TABLE_OPTIONS.BANK_RATES,
        label: (
            <FormattedMessage
                id="web-components.ProductTableBestMortgageRates.BestBankRatesTab"
                defaultMessage="Best bank rates"
            />
        ),
    },
];

const Container = styled.div`
    .hidden-table {
        display: none;
    }
`;

ProductTableBestMortgageRates.blockKey = 'rh/product-table-best-mortgage-rates';
ProductTableBestMortgageRates.requiresLayoutRow = true;
ProductTableBestMortgageRates.supportedPageTypes = [
    PAGE_TEMPLATES.mortgages,
    PAGE_TEMPLATES.mtgAgentsList,
];

export default observer(ProductTableBestMortgageRates);
